/**
 * t462u: the phone person sheet's "Two-factor sign-in" section, for workspace Owners only (the API checks too):
 * "Require two-factor sign-in", whether they've set it up, and reset for a lost phone. Same wording as the desktop
 * Users page and the iOS app (packages/shared/src/mfa.ts).
 */
import { MEMBER_MFA_TEXT, canRequireForSelf, memberMfaNote, type PersonProfile } from '@tradeoutlet/shared';
import { useWorkspaceId } from '@/lib/hooks';
import { useMemberTwoFactor } from '@/features/auth/mfa';

const head = 'mb-1 px-5 text-xs font-semibold uppercase tracking-wide text-slate-500';

export function PersonTwoFactor({ p }: { p: PersonProfile }) {
  const ws = useWorkspaceId();
  const show = Boolean(p.can.manage_two_factor && p.active && !p.is_external);
  const { q, busy, setRequired, reset } = useMemberTwoFactor(ws, p.user_id, show);
  const m = q.data;
  if (!show || q.isError) return null;
  const locked = Boolean(m && p.is_me && !m.required && !canRequireForSelf(m));
  return (
    <section className="border-t border-slate-100 py-3 dark:border-slate-800" data-testid="person-two-factor">
      <h3 className={head}>{MEMBER_MFA_TEXT.title}</h3>
      {!m ? <p className="px-5 text-sm text-slate-500">…</p> : (
        <div className="mx-5 overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700">
          <button type="button" role="switch" aria-checked={m.required} disabled={busy !== null || locked} onClick={() => void setRequired(!m.required)}
            className="flex min-h-[48px] w-full items-center gap-3 px-4 text-left text-base active:bg-slate-100 disabled:opacity-60 dark:active:bg-slate-800" data-testid="person-two-factor-switch">
            <span className="flex-1">{MEMBER_MFA_TEXT.switchLabel}</span>
            <span aria-hidden className={`relative h-6 w-10 shrink-0 rounded-full transition ${m.required ? 'bg-accent ring-1 ring-inset ring-ink' : 'bg-slate-300 dark:bg-slate-700'}`}>
              <span className={`absolute top-0.5 h-5 w-5 rounded-full shadow transition ${m.required ? 'left-[18px] bg-ink' : 'left-0.5 bg-white'}`} />
            </span>
          </button>
          <p className="border-t border-slate-100 px-4 py-2 text-sm text-slate-500 dark:border-slate-800" data-testid="person-two-factor-status">{memberMfaNote(m, p.is_me)}</p>
          {m.set_up && !p.is_me && (
            <button type="button" disabled={busy !== null} onClick={() => void reset()} className="flex min-h-[48px] w-full items-center border-t border-slate-100 px-4 text-left text-base text-rose-600 active:bg-slate-100 dark:border-slate-800 dark:active:bg-slate-800" data-testid="person-two-factor-reset">
              {MEMBER_MFA_TEXT.reset}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
